/** Shared Náhľad period-mode selection defaults (trng001 / calb001 / mntn040). */

export const PREVIEW_PERIOD_OPTIONS = Object.freeze([
  'Všetko',
  'Za deň',
  'Za mesiac',
  'Za rok',
  'Vlastné',
]);

export const PREVIEW_PERIOD_DEFAULT = 'Všetko';

/**
 * Default secondary date value after changing period mode.
 * Day/month/year use "now" (Calendar view selects the right granularity).
 * Všetko / Vlastné leave the control empty until the user picks a range.
 *
 * @param {string} timePeriod
 * @param {Date} [now]
 * @returns {Date|null}
 */
export function defaultDateForPeriod(timePeriod, now = new Date()) {
  if (timePeriod === 'Za deň' || timePeriod === 'Za mesiac' || timePeriod === 'Za rok') {
    const d = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds());
  }
  return null;
}

/**
 * PrimeReact Calendar props for the selected period mode.
 * @param {string} timePeriod
 * @returns {Record<string, unknown>}
 */
export function datePickerPropsForPeriod(timePeriod) {
  if (timePeriod === 'Za deň') return { dateFormat: 'dd.mm.yy' };
  if (timePeriod === 'Za mesiac') return { view: 'month', dateFormat: 'mm.yy' };
  if (timePeriod === 'Za rok') return { view: 'year', dateFormat: 'yy' };
  if (timePeriod === 'Vlastné') return { dateFormat: 'dd.mm.yy', selectionMode: 'range' };
  return { hidden: true };
}

/**
 * Canonical YYYY-MM-DD for a Date in local time.
 * Never use Date#toISOString().slice(0, 10) — UTC shifts the calendar day in CEST/CET.
 * @param {Date|null|undefined} d
 * @returns {string|null}
 */
export function toLocalDateString(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Serialize preview `date` query param (single Date | YYYY-MM-DD | range) for OTK list/stats.
 * Matches PreviewOTK / mobile otkPreviewQuery local-date contract.
 * @param {Date|string|Array|null|undefined} date
 * @returns {string|null}
 */
export function serializePreviewDateForApi(date) {
  if (date == null || date === '') return null;
  if (Array.isArray(date)) {
    const parts = date.map((d) => {
      if (d instanceof Date) return toLocalDateString(d);
      if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
      return d == null ? null : String(d);
    });
    if (parts.length < 2 || parts[0] == null || parts[1] == null) return null;
    return JSON.stringify(parts);
  }
  if (date instanceof Date) return toLocalDateString(date);
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) return date.slice(0, 10);
  return String(date);
}

/**
 * Parse YYYY-MM-DD (or ISO prefix) as a local calendar Date (not UTC midnight).
 * @param {string|null|undefined} value
 * @returns {Date|null}
 */
export function parseLocalDateString(value) {
  if (value == null || value === '') return null;
  const str = String(value).trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const y = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  const d = new Date(y, month, day);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Month key mm.yyyy for display/test assertions (local).
 * @param {Date} d
 * @returns {string|null}
 */
export function toMonthYearLabel(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${m}.${d.getFullYear()}`;
}
